<template>
  <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
  <vSelect
    :clearable="clearable"
    :options="options"
    :filterable="false"
    label="name"
    :loading="loading"
    :placeholder="placeholder"
    @open="onOpen"
    @close="onClose"
    @search="debouncedHandleSearch"
    @input="handleSelectedValue"
    v-model="selectedValue"
  >
    <template #list-footer>
      <li v-show="hasNextPage" ref="load" class="loader">
        {{ $t('pmd.uiComponents.loadingMoreOptions') }}
      </li>
    </template>
  </vSelect>
  <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
</template>

<script>
import { defineComponent } from 'vue'

import vSelect from 'vue-select'
import axios from 'axios'
import debounce from 'lodash-es/debounce'

export default defineComponent({
  props: ['apiUrl', 'clearable', 'modelValue', 'placeholder', 'optionsMapper'],
  name: 'UISelectPaginated',
  components: {
    vSelect,
  },
  emits: ['update:modelValue'],
  data() {
    return {
      observer: null,
      pageSize: 20,
      page: 0,
      search: '',
      loading: true,
      options: [],
      totalCount: 0,
      selectedValue: this.modelValue,
    }
  },
  computed: {
    hasNextPage: function () {
      return this.options.length < this.totalCount
    },
    debouncedHandleSearch() {
      return debounce(this.handleSearch, 800)
    },
  },
  watch: {
    search: async function () {
      this.loading = true
      this.page = 0
      this.options = []
      await this.fetchOptions()
      this.attachObserver()
      this.loading = false
    },
    selectedValue(newValue) {
      this.$emit('update:modelValue', newValue)
    },
    modelValue(changedValue) {
      this.selectedValue = changedValue
    },
  },
  mounted() {
    this.loading = true
    this.page = 0
    this.options = []
    this.fetchOptions()
    this.observer = new IntersectionObserver(this.infiniteScroll)
    this.loading = false
  },
  methods: {
    async fetchDataFromApi(page, pageSize, search = '') {
      try {
        const response = await axios.get(this.apiUrl, {
          params: {
            limit: pageSize,
            offset: page * pageSize,
            search: search || '',
          },
        })
        return response?.data
      } catch (error) {
        console.error('Error fetching options:', error)
      }
    },
    async fetchOptions() {
      try {
        const data = await this.fetchDataFromApi(this.page, this.pageSize, this.search)

        if (!data) {
          return
        }
        const newOptions = this.optionsMapper ? this.optionsMapper(data.rows) : data.rows
        this.options = [...this.options, ...newOptions]
        this.totalCount = data.count
      } catch (error) {
        console.error('Error fetching options:', error)
      }
    },
    async onOpen() {
      await this.attachObserver()
    },
    onClose() {
      this.observer.disconnect()
    },
    async handleSearch(searchQuery) {
      this.search = searchQuery
    },
    async attachObserver() {
      if (this.hasNextPage) {
        await this.$nextTick()
        const loadTarget = this.$refs.load
        if (loadTarget instanceof Element) {
          this.observer.observe(loadTarget)
        } else {
          console.warn('⚠️ loadTarget is not an Element:', loadTarget)
        }
      }
    },
    async infiniteScroll([{ isIntersecting, target }]) {
      if (isIntersecting) {
        const ul = target.offsetParent
        const scrollTop = target.offsetParent.scrollTop
        this.page += 1
        await this.$nextTick()
        await this.fetchOptions()
        ul.scrollTop = scrollTop
      }
    },
    handleSelectedValue(value) {
      this.selectedValue = value
    },
  },
})
</script>

<style scoped>
.loader {
  text-align: center;
  color: #bbbbbb;
}
</style>
